// The illustrated swatch for every collapsed-ink picker in the configurator —
// `cover_ink`, `interior_ink` and `print_ink`. ONE control renders all three on
// every product family, so no field and no family can depict the same N/M ink
// code differently from its neighbours.
//
// The model every swatch draws: an N/M code names the TWO FACES of ONE piece, so
// the faces are STACKED — the first face (outside / front) above the second
// (inside / back). Side-by-side is reserved for the single value that really is
// two distinct pieces: the interior-only `mixed`, where color distributes across
// PAGES rather than across the faces of one leaf.
//
// The only per-piece difference is the FOLD. A saddle-stitched, perfect-bound or
// hard-bound cover is one wide sheet creased at the spine, so each of its faces
// is a two-panel spread (back cover | spine | front cover) with a hairline
// crease. Every other piece — a coil / wire-O cover, which is a loose front and
// back sheet with no spine; an interior leaf; a flat sheet — has nothing to
// fold, so each face draws as one unbroken panel. Drawing a crease there would
// invent a fold that isn't in the job.
//
// Bar colors are PURELY REPRESENTATIONAL print inks (like chart-series colors),
// not brand chrome, so they're literals here; the sheet fill + border DO use
// palette tokens.

import type { JSX } from 'react';

const INK_PROCESS_BARS = ['#00A9E0', '#E5006D', '#F6C000']; // C / M / Y — "full color"
const INK_BLACK_BAR = '#2B2D2F'; // near-black ink — "black"

// Ink level (a single `<first>` or `<second>` digit) → its face's bars. An empty
// list means an unimaged face, drawn dashed.
const INK_LEVEL_BARS: Record<string, string[]> = {
  '4': INK_PROCESS_BARS, // full process color
  '1': [INK_BLACK_BAR, INK_BLACK_BAR, INK_BLACK_BAR], // black only
  '0': [], // blank / not imaged
};

// Every swatch shares one viewBox and one CSS footprint, so switching a field or
// a family between creased and uncreased never reflows the card grid.
const VIEW_W = 72;
const VIEW_H = 56;
const FACE_X = 3;
const FACE_W = 66;
const FACE_H = 23;
const FACE_Y = [3, 30]; // first face above second

// One face of the piece: the sheet, its content bars, and — on a folded cover
// only — the spine crease with the bars repeated in both panels (each half is a
// cover panel, imaged the same). An EMPTY `bars` draws an unprinted face: dashed
// outline, and it KEEPS the crease so a blank inside still reads as the same
// folded sheet.
function InkFace({
  y,
  bars,
  creased,
}: {
  y: number;
  bars: string[];
  creased: boolean;
}): JSX.Element {
  const mid = FACE_X + FACE_W / 2;
  const pad = 4;
  const panels = creased
    ? { xs: [FACE_X + pad, mid + pad - 0.5], w: FACE_W / 2 - pad - 2.5 }
    : { xs: [FACE_X + pad + 1], w: FACE_W - 2 * (pad + 1) };
  const step = (FACE_H - 11) / 3;
  return (
    <>
      <rect
        x={FACE_X}
        y={y}
        width={FACE_W}
        height={FACE_H}
        rx={2.5}
        // Palette tokens must ride in `style` — CSS var() does NOT resolve as an
        // SVG presentation attribute (fill="var(...)" renders empty).
        style={{ fill: 'var(--color-surface)', stroke: 'var(--color-border)' }}
        strokeWidth={1}
        strokeDasharray={bars.length === 0 ? '3 2' : undefined}
      />
      {creased && (
        /* the spine crease — one hairline, deliberately subtle */
        <line
          x1={mid}
          y1={y + 3.5}
          x2={mid}
          y2={y + FACE_H - 3.5}
          style={{ stroke: 'var(--color-border)' }}
          strokeWidth={1}
        />
      )}
      {panels.xs.flatMap((x) =>
        bars.map((c, i) => (
          <rect
            key={`${x}-${i}`}
            x={x}
            y={y + 5.5 + i * step}
            width={panels.w}
            height={2.6}
            rx={1.3}
            fill={c}
          />
        )),
      )}
    </>
  );
}

// The `<first>_<second>` code as two stacked faces of one piece.
function InkFacesSwatch({ value, creased }: { value: string; creased: boolean }): JSX.Element {
  const [first = '0', second = '0'] = value.split('_');
  return (
    <svg
      className="ink-swatch"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      aria-hidden="true"
      focusable="false"
    >
      <InkFace y={FACE_Y[0]!} bars={INK_LEVEL_BARS[first] ?? []} creased={creased} />
      <InkFace y={FACE_Y[1]!} bars={INK_LEVEL_BARS[second] ?? []} creased={creased} />
    </svg>
  );
}

// The interior-only `mixed` value (some color pages, some B&W) can't be an N/M
// code — color distributes across PAGES, not across a leaf's two faces — so it
// shows two distinct portrait pages, one full-color and one black, reading as "a
// mix of page types" rather than as one piece's two sides.
const MIXED_PAGE_W = 26;
const MIXED_PAGE_H = 40;
function MixedPagesSwatch(): JSX.Element {
  return (
    <svg
      className="ink-swatch"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      aria-hidden="true"
      focusable="false"
    >
      {[
        { x: 8, bars: INK_PROCESS_BARS },
        { x: 38, bars: [INK_BLACK_BAR, INK_BLACK_BAR, INK_BLACK_BAR] },
      ].map(({ x, bars }) => (
        <g key={x}>
          <rect
            x={x}
            y={8}
            width={MIXED_PAGE_W}
            height={MIXED_PAGE_H}
            rx={3}
            style={{ fill: 'var(--color-surface)', stroke: 'var(--color-border)' }}
            strokeWidth={1}
          />
          {bars.map((c, i) => (
            <rect
              key={i}
              x={x + 5}
              y={18 + i * 9}
              width={MIXED_PAGE_W - 10}
              height={3.5}
              rx={1.5}
              fill={c}
            />
          ))}
        </g>
      ))}
    </svg>
  );
}

// Which cover-bearing families get a FOLDED cover. Saddle-stitch, perfect-bound
// and hard-bound covers are all one wide sheet wrapped around the book (stitched
// through the spine, glued at it, or cased around the boards). Presentation-only
// and schema-coupled, alongside SchemaForm's SECTION_LABEL_BY_FAMILY.
export const FOLDED_COVER_FAMILIES = new Set([
  'saddle_stitch_book',
  'perfect_bound_book',
  'hard_bound_book',
]);

// Cover-bearing families whose cover does NOT fold — a coil / wire-O book has a
// loose front sheet and a loose back sheet and no spine. Spelled out rather than
// left to fall through, so adding a binding forces the fold question to be
// answered: ink-swatch-single-control.test.ts fails on a cover-bearing family
// that appears in neither set.
export const FLAT_COVER_FAMILIES = new Set(['coil_or_wire_o_book']);

// The enum fields whose options render as illustrated swatch cards. The renderer
// dispatches on membership here, so adding a fourth collapsed-ink field is one
// entry and it inherits the same depiction — there is no second swatch to write.
export const INK_SWATCH_FIELD_KEYS = ['cover_ink', 'print_ink', 'interior_ink'] as const;

export function isInkSwatchField(key: string): boolean {
  return (INK_SWATCH_FIELD_KEYS as readonly string[]).includes(key);
}

// `family` is the line's product_type, which only `cover_ink` consults: a cover
// is creased on the wrap bindings and flat on coil / wire-O. Interiors and flat
// work never fold, so they ignore it.
export function InkSwatch({
  fieldKey,
  value,
  family,
}: {
  fieldKey: string;
  value: string;
  family: unknown;
}): JSX.Element {
  if (value === 'mixed') return <MixedPagesSwatch />;
  const creased =
    fieldKey === 'cover_ink' && typeof family === 'string' && FOLDED_COVER_FAMILIES.has(family);
  return <InkFacesSwatch value={value} creased={creased} />;
}
